import React, { useCallback, useEffect, useState } from "react";
import AppContainer from "../../common/app_container";
import DataTable from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import Alert, { AlertProps } from "../../common/alert";
import { SubmitHandler, useForm } from "react-hook-form";
import { InvitationCode, InvitationCodeFormData } from "@src/model/invitation_code";
import { useInvitationCodeAPI } from "@src/api/invitation_code_api";

const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "Never";
    try {
        return new Date(dateString).toLocaleString();
    } catch (e) {
        return "Invalid Date";
    }
};

const InvitationCodeIndex: React.FC = () => {
    const [data, setData] = useState<InvitationCode[]>([]);
    const [status, setStatus] = useState<AlertProps | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [actionLoading, setActionLoading] = useState<string | null>(null);
    const { getCodes, createCode, deleteCode, disableCode } = useInvitationCodeAPI();
    const navigate = useNavigate();

    const { register, handleSubmit, reset, formState: { errors } } = useForm<InvitationCodeFormData>();
    //
    const fetchData = useCallback(async () => {
        setLoading(true);
        setStatus(null);
        try {
            const codes = await getCodes();
            setData(codes.filter((code: { status: string; }) => code.status !== "DELETED"));
        } catch (error: any) {
            setStatus({ type: "error", message: "Failed to load invitation codes: " + error.message });
        } finally {
            setLoading(false);
        }
    }, [getCodes]);

    useEffect(() => {
        fetchData();
    }, []);


    const handleDelete = async (id: string) => {
        if (!window.confirm("Are you sure you want to delete this code? This is a soft delete.")) return;
        setActionLoading(`delete-${id}`);
        setStatus(null);
        try {
            await deleteCode(id);
            setStatus({ type: "success", message: "Code deleted successfully." });
            fetchData();
        } catch (error: any) {
            setStatus({ type: "error", message: "Failed to delete code: " + error.message });
        } finally {
            setActionLoading(null);
        }
    };

    const handleDisable = async (id: string) => {
        if (!window.confirm("Are you sure you want to disable this code?")) return;
        setActionLoading(`disable-${id}`);
        setStatus(null);
        try {
            await disableCode(id);
            setStatus({ type: "success", message: "Code disabled successfully." });
            fetchData();
        } catch (error: any) {
            setStatus({ type: "error", message: "Failed to disable code: " + error.message });
        } finally {
            setActionLoading(null);
        }
    };

    const handleEdit = (id: string) => {
        navigate(`/invitation-codes/edit/${id}`);
    };

    const onCreateSubmit: SubmitHandler<InvitationCodeFormData> = async (formData) => {
        setStatus(null);
        setLoading(true);
        const payload: InvitationCodeFormData = {
            ...formData,
            max_usages: formData.max_usages ? Number(formData.max_usages) : 0,
            status: "ACTIVE",
        };
        try {
            await createCode(payload);
            setStatus({ type: "success", message: "Invitation code created successfully." });
            reset();
            fetchData();
        } catch (error: any) {
            setStatus({ type: "error", message: "Failed to create code: " + error.message });
            setLoading(false);
        }
    };

    const columns = [
        { name: "Code", selector: (row: InvitationCode) => row.code, sortable: true },
        {
            name: "Status",
            selector: (row: InvitationCode) => row.status,
            sortable: true,
            cell: (row: InvitationCode) => (
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${row.status === "ACTIVE" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"
                    }`}>
                    {row.status}
                </span>
            ),
        },
        {
            name: "Usage",
            selector: (row: InvitationCode) => `${row.current_usages} / ${row.max_usages === 0 ? "∞" : row.max_usages}`,
            sortable: true,
        },
        { name: "Created", selector: (row: InvitationCode) => formatDate(row.created_at), sortable: true },
        { name: "Last Used", selector: (row: InvitationCode) => formatDate(row.last_used_at), sortable: true },
        {
            name: "Actions",
            // button: true,
            cell: (row: InvitationCode) => (
                <div className="flex space-x-1">
                    <button
                        onClick={() => handleEdit(row.id)}
                        disabled={!!actionLoading}
                        className="p-1 bg-blue-500 text-white rounded hover:bg-blue-700 text-xs disabled:opacity-50"
                        aria-label="Edit"
                    >
                        Edit
                    </button>
                    {row.status === "ACTIVE" && (
                        <button
                            onClick={() => handleDisable(row.id)}
                            disabled={actionLoading === `disable-${row.id}`}
                            className="p-1 bg-yellow-500 text-white rounded hover:bg-yellow-700 text-xs disabled:opacity-50"
                            aria-label="Disable"
                        >
                            Disable
                        </button>
                    )}
                    <button
                        onClick={() => handleDelete(row.id)}
                        disabled={actionLoading === `delete-${row.id}`}
                        className="p-1 bg-red-500 text-white rounded hover:bg-red-700 text-xs disabled:opacity-50"
                        aria-label="Delete"
                    >
                        Delete
                    </button>
                </div>
            ),
        },
    ];


    return (
        <AppContainer>
            <div className="p-6">
                <h1 className="text-2xl font-semibold mb-1">Invitation Codes</h1>
                <p className="text-sm text-gray-600 mb-4">Manage invitation codes for talent and creator onboarding.</p>

                {status && <Alert {...status} onClose={() => setStatus(null)} className="mb-4" />}

                <form onSubmit={handleSubmit(onCreateSubmit)} className="bg-white p-4 rounded shadow border mb-6">
                    <h2 className="text-lg font-semibold mb-3">Create New Code</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                        <div>
                            <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1">Invitation Code</label>
                            <input
                                type="text"
                                id="code"
                                {...register("code", {
                                    required: "Code is required",
                                    onChange: (e) => {
                                        e.target.value = e.target.value.toUpperCase();
                                    }
                                })}

                                className={`block w-full p-2 border rounded shadow-sm text-sm ${errors.code ? "border-red-500" : "border-gray-300"} focus:ring-blue-500 focus:border-blue-500`}
                                placeholder="E.g., TALENT2025"
                            />
                            {errors.code && <p className="text-red-500 text-xs mt-1">{String(errors.code.message)}</p>}
                        </div>

                        <div>
                            <label htmlFor="max_usages" className="block text-sm font-medium text-gray-700 mb-1">Max Usages (0 for unlimited)</label>
                            <input
                                type="number"
                                id="max_usages"
                                min="0"
                                {...register("max_usages", { valueAsNumber: true, min: { value: 0, message: "Must be 0 or greater" } })}
                                className={`block w-full p-2 border rounded shadow-sm text-sm ${errors.max_usages ? "border-red-500" : "border-gray-300"} focus:ring-blue-500 focus:border-blue-500`}
                                placeholder="1000"
                            />
                            {errors.max_usages && <p className="text-red-500 text-xs mt-1">{String(errors.max_usages.message)}</p>}
                        </div>

                        <div>
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full md:w-auto px-4 py-2 bg-blue-600 text-white rounded shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 disabled:opacity-50 text-sm font-medium"
                            >
                                + Create Code
                            </button>
                        </div>
                    </div>
                </form>

                <div className="border rounded shadow-sm">
                    <DataTable
                        columns={columns}
                        data={data}
                        progressPending={loading}
                        progressComponent={<div className="py-10 text-center">Loading...</div>}
                        noDataComponent={<div className="py-10 text-center text-gray-500">No invitation codes found.</div>}
                        pagination
                        highlightOnHover
                        striped
                        customStyles={{
                            headCells: { style: { fontSize: "13px", fontWeight: "bold", color: "#4a5568", backgroundColor: "#f7fafc" } },
                            cells: { style: { fontSize: "13px" } },
                        }}
                    />
                </div>
            </div>
        </AppContainer>
    );
};

export default InvitationCodeIndex;
